import React, { useState, useEffect } from 'react';
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import EmployeeEditModal from '../../../../features/portal/MeSection/MyDocuments/EmployeeEditModal';
import { Card, Row, Col, Collapse } from "react-bootstrap";
import { fetchProfileData, createProfileData } from '../../../../redux/slice/profileUpdateSupportSlice';
import { useDispatch, useSelector } from 'react-redux';
import { toast, ToastContainer } from "react-toastify";
import { fetchMyProfile } from '../../../../redux/slice/myProfileSlice';
import { fetchTicketById } from '../../../../redux/slice/supportSlice';

function InfoRow({ label, value }) {
    let finalValue;

    // Check if the value is a valid React element (like JSX badges/fragments)
    if (React.isValidElement(value)) {
        finalValue = value;
    } else {
        // If it's a standard string, number, or null element
        const cleanValue = typeof value === 'string' ? value.trim() : String(value ?? "");
        finalValue = cleanValue !== "" ? cleanValue : "N/A";
    }
    return (
        <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="profile-label">{label}:</span>
            <p className="profile-label-value text-dark m-0">
                {finalValue ?? "N/A"}
            </p>
        </div>
    );
}

InfoRow.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
};

function InfoRowPersonal({ label, value, className = "" }) {
    let finalValue;
    if (React.isValidElement(value)) {
        finalValue = value;
    } else {
        const cleanValue = typeof value === 'string' ? value.trim() : String(value ?? "");
        finalValue = cleanValue !== "" ? cleanValue : "N/A";
    }
    return (
        <div className={`col-md-6 d-flex align-items-center justify-content-between mb-2 unique-personal-row-item ${className}`}>
            <span className="d-inline-flex align-items-center unique-personal-label">
                {label}:
            </span>
            <p className="text-dark m-0 unique-personal-value text-right">
                {finalValue ?? "N/A"}
            </p>
        </div>
    );
}

InfoRowPersonal.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    className: PropTypes.string,
};

// fallback helpers
const getDisplayValue = (val) => (val && val.trim() !== "") ? val : 'N/A';
const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString() : "—";
const extractYear = (dateStr) => dateStr ? new Date(dateStr).getFullYear() : null;
const joinAddress = (addr) => {
    if (!addr) return "";
    return [
        addr.address1,
        addr.address2,
        addr.address3,
        addr.city,
        addr.state,
        addr.zip
    ]
        .map(val => val?.trim())
        .filter(Boolean)
        .join(", ");
};

const getFormattedDuration = (from, to) => `${extractYear(from) ?? '—'} - ${extractYear(to) ?? '—'}`;

export default function ProfileUpdateSectionEmployee({ ticket }) {
    const dispatch = useDispatch();
    const { Myprofile } = useSelector((state) => state.Myprofile ?? {});
    const { profileUpdate } = useSelector((state) => state.profileUpdate ?? {});
    const [openSections, setOpenSections] = useState({
        about: false,
        personal: false,
        bank: false,
        family: false,
        nominee: false,
        education: false,
        experience: false,
        address: false,
        additional: false,
        professional: false,
    });
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentFields, setCurrentFields] = useState([]);
    const [modalTitle, setModalTitle] = useState("");
    const [modalSize, setModalSize] = useState("md");
    const [modalType, setModalType] = useState("");
    const [accumulatedSections, setAccumulatedSections] = useState([]);

    const {
        register,
        handleSubmit,
        reset,
        watch,
        control,
        formState: { errors },
    } = useForm();
    const { id: ticketId, status: ticketStatus } = ticket;
    const isTicketCreated = ticketStatus?.toLowerCase() === "created";
    const isTicketInProgress = ticketStatus?.toLowerCase() === "in_progress";
    const isTicketValid = ticketStatus?.toLowerCase() === "approved";
    const isTicketRework = ticketStatus?.toLowerCase() === "rework_required";
    const hitMsg = ticket?.status?.toLowerCase() === "verified" || ticket?.status?.toLowerCase() === "closed";
    const hitProfileApi = ticket?.status?.toLowerCase() === "updated" || ticket?.status?.toLowerCase() === "rework_required";
    const showEdit = ticketStatus?.toLowerCase() === "approved" || ticketStatus?.toLowerCase() === "rework_required";

    useEffect(() => {
        if (!ticketId) return;
        if (hitProfileApi) {
            dispatch(fetchProfileData(ticketId));
        } else if (!hitMsg) {
            dispatch(fetchMyProfile());
        }

    }, [dispatch, ticketId, hitMsg, hitProfileApi]);
    // Unify the extraction of the raw staging updates array safely
    const stagingArray = Array.isArray(profileUpdate)
        ? profileUpdate
        : profileUpdate?.data || [];

    // Extract out all relevant section objects from the API payload list
    const stagedPersonal = stagingArray.find(item => item.sectionType === "PERSONAL_INFORMATION")?.newData;
    const stagedBank = stagingArray.find(item => item.sectionType === "BANK_DETAILS")?.newData;
    const stagedFamily = stagingArray.find(item => item.sectionType === "INSURANCE_DEPENDENT")?.newData;
    const stagedNominee = stagingArray.find(item => item.sectionType === "NOMINEE")?.newData;
    const stagedAddress = stagingArray.find(item => item.sectionType === "ADDRESS")?.newData;
    const stagedEducation = stagingArray.find(item => item.sectionType === "EDUCATION")?.newData;
    const stagedExperience = stagingArray.find(item => item.sectionType === "EXPERIENCE")?.newData;
    const stagedAdditional = stagingArray.find(item => item.sectionType === "ADDITIONAL_DETAILS")?.newData;
    const stagedProfessional = stagingArray.find(item => item.sectionType === "PROFESSIONAL_REFERENCE")?.newData;

    // Assemble displayData securely preserving both baselines and updates
    const displayData = {
        ...Myprofile,

        // Object sections
        personalDetail: stagedPersonal ? { ...stagedPersonal } : { ...Myprofile?.personalDetail },
        bankDetails: stagedBank ? { ...stagedBank } : { ...Myprofile?.bankDetails },
        additionalDetails: stagedAdditional ? { ...stagedAdditional } : { ...Myprofile?.additionalDetails },
        //professionalreference: stagedProfessional ? { ...stagedProfessional } : { ...Myprofile?.professionalReferences },

        // Array / List sections 
        // (Handles both scenarios where newData itself is an array or an object wrapped layout)
        nomineeDetails: stagedNominee
            ? (Array.isArray(stagedNominee)
                ? stagedNominee
                : (stagedNominee.nomineeDetails ? stagedNominee.nomineeDetails : [stagedNominee]))
            : (Myprofile?.nomineeDetails || []),

        // professionalReferences: stagedProfessional?.professionalReferences
        //     ? stagedProfessional.professionalReferences
        //     : (Myprofile?.professionalReferences || []),
        
        professionalReferences: stagedProfessional
            ? (Array.isArray(stagedProfessional)
                ? stagedProfessional
                : (stagedProfessional.professionalReferences ? stagedProfessional.professionalReferences : [stagedProfessional]))
            : (Myprofile?.professionalReferences || []),

        insuranceDependentDetails: stagedFamily
            ? (Array.isArray(stagedFamily)
                ? stagedFamily
                : (stagedFamily.insuranceDependentDetails ? stagedFamily.insuranceDependentDetails : [stagedFamily]))
            : (Myprofile?.insuranceDependentDetails || []),

        addresses: stagedAddress
            ? (Array.isArray(stagedAddress) ? stagedAddress : stagedAddress.addresses || [])
            : (Myprofile?.addresses || []),

        educationDetails: stagedEducation
            ? (Array.isArray(stagedEducation)
                ? stagedEducation
                : (stagedEducation.educationDetails ? stagedEducation.educationDetails : [stagedEducation]))
            : (Myprofile?.educationDetails || []),

        employmentHistory: stagedExperience
            ? (Array.isArray(stagedExperience)
                ? stagedExperience
                : (stagedExperience.employmentHistory ? stagedExperience.employmentHistory : [stagedExperience]))
            : (Myprofile?.employmentHistory || [])
    };
    const addressList = displayData?.addresses || [];

    const permanentAddressData = addressList.find(addr => addr.addressType === "PERMANENT");
    const currentAddressData = addressList.find(addr => addr.addressType !== "PERMANENT" || !addr.addressType);

    const bankFields = [
        {
            label: "Account Holder Name",
            name: "accountHolderName",
            type: "text",
            rules: {
                required: "Account holder name is required",
                minLength: { value: 3, message: "Name is too short" },
                pattern: {
                    value: /^[a-zA-Z\s]*$/,
                    message: "Only alphabets are allowed",
                },
            },
        },
        {
            label: "Account Number",
            name: "accountNumber",
            type: "text",
            maxLength: 18,
            rules: {
                required: "Account number is required",
                minLength: { value: 9, message: "Minimum 9 digits required" },
                maxLength: { value: 18, message: "Maximum 18 digits allowed" },
                pattern: { value: /^\d+$/, message: "Only digits are allowed" },
            },
        },
        {
            label: "Bank Name",
            name: "bankName",
            type: "text",
            rules: { required: "Bank name is required" },
        },
        {
            label: "IFSC Code",
            name: "ifscCode",
            type: "text",
            rules: {
                required: "IFSC code is required",
                pattern: {
                    value: /^[A-Z]{4}0[A-Z0-9]{6}$/,
                    message: "Invalid IFSC (e.g., SBIN0123456)",
                },
            },
        },
        {
            label: "Branch Name",
            name: "branchName",
            type: "text",
            rules: { required: "Branch name is required" },
        },
    ];
    const educationFields = [
        {
            label: "Qualification",
            name: "degreeName",
            type: "text",
            rules: {
                required: "Qualification is required",
                minLength: { value: 2, message: "Min 2 characters required" }
            }
        },
        {
            label: "Field of Study",
            name: "fieldOfStudy",
            type: "text",
            rules: {
                required: "Field of study is required",
                pattern: {
                    value: /^[a-zA-Z\s\.]*$/,
                    message: "Numbers and special characters not allowed"
                }
            }
        },
        {
            label: "Institution Name",
            name: "institutionName",
            type: "text",
            rules: { required: "Institution name is required" }
        },
        {
            label: "Location",
            name: "location",
            type: "text",
            rules: {
                pattern: {
                    value: /^[a-zA-Z\s,]*$/,
                    message: "Invalid format for location"
                }
            }
        },
        {
            label: "Grade / CGPA",
            name: "gradeOrcgpa",
            type: "text",
            rules: { required: "Grade/CGPA is required" }
        },
        {
            label: "From Date",
            name: "fromDate",
            type: "date",
            rules: { required: "Start date is required" }
        },
        {
            label: "To Date",
            name: "toDate",
            type: "date",
            rules: {
                required: "End date is required",
                validate: (value, formValues) => {
                    const start = formValues.fromDate;
                    if (!start) return true;
                    return new Date(value) >= new Date(start) || "End date cannot be earlier than start date";
                }
            }
        },
        {
            label: "Roll Number",
            name: "rollNumber",
            type: "text"
        }
    ];

    const experienceFields = [
        {
            label: "Employer Name",
            name: "previousEmployerName",
            type: "text",
            rules: {
                required: "Employer name is required",
                minLength: { value: 2, message: "Name is too short" }
            }
        },
        {
            label: "Designation",
            name: "designation",
            type: "text",
            rules: {
                required: "Designation is required",
                pattern: {
                    value: /^[a-zA-Z\s]*$/,
                    message: "Numbers and special characters not allowed"
                }
            }
        },
        {
            label: "Department",
            name: "department",
            type: "text",
            rules: {
                pattern: {
                    value: /^[a-zA-Z\s]*$/,
                    message: "Numbers and special characters not allowed"
                }
            }
        },
        {
            label: "Employee Code",
            name: "employeeCode",
            type: "text"
        },
        {
            label: "From Date",
            name: "employmentPeriodFrom",
            type: "date",
            rules: { required: "Start date is required" }
        },
        {
            label: "To Date",
            name: "employmentPeriodTo",
            type: "date",
            rules: {
                required: "End date is required",
                validate: (value) => {
                    const fromDate = watch("employmentPeriodFrom");
                    if (!fromDate) return true;
                    return new Date(value) >= new Date(fromDate) || "End date cannot be earlier than start date";
                }
            }
        },
        {
            label: "Manager Name",
            name: "managerName",
            type: "text",
            rules: {
                pattern: {
                    value: /^[a-zA-Z\s]*$/,
                    message: "Numbers and special characters not allowed"
                }
            }
        },
        {
            label: "Manager Email",
            name: "managerEmail",
            type: "text",
            rules: {
                pattern: { value: /^\S+@\S+\.\S+$/, message: "Invalid email format" }
            }
        },
        {
            label: "Manager Contact",
            name: "mobileNumber",
            type: "text",
            rules: {
                pattern: { value: /^[0-9]{10}$/, message: "Must be 10 digits" }
            }
        }
    ];

    const addressFields = [
        {
            label: "Address Line 1",
            name: "address1",
            type: "text",
            rules: { required: "Address Line 1 is required" }
        },
        {
            label: "Address Line 2",
            name: "address2",
            type: "text"

        },
        {
            label: "Address Line 3",
            name: "address3",
            type: "text"

        },
        {
            label: "City",
            name: "city",
            type: "text",
            rules: {
                required: "City is required",
                pattern: {
                    value: /^[a-zA-Z\s]*$/,
                    message: "Numbers and special characters are not allowed"
                }
            }
        },
        {
            label: "State",
            name: "state",
            type: "text",
            rules: {
                required: "State is required",
                pattern: {
                    value: /^[a-zA-Z\s]*$/,
                    message: "Numbers and special characters are not allowed"
                }
            }
        },
        {
            label: "Zip Code",
            name: "zip",
            type: "text",
            rules: {
                required: "Zip Code is required",
                pattern: {
                    value: /^\d{6}$/,
                    message: "Zip code must be exactly 6 digits"
                }
            }
        },
        {
            label: "Country",
            name: "country",
            type: "text",
            rules: {
                required: "Country is required",
                pattern: {
                    value: /^[a-zA-Z\s]*$/,
                    message: "Numbers and special characters are not allowed"
                }
            }
        },
    ];

    const nomineeFields = [
        {
            label: "Nominee Name",
            name: "nomineeName",
            type: "text",
            rules: {
                required: "Nominee Name is required",
                pattern: {
                    value: /^[a-zA-Z\s]*$/,
                    message: "Numbers and special characters are not allowed",
                },
                minLength: { value: 2, message: "Name must be at least 2 characters" },
            },
        },
        {
            label: "Relationship",
            name: "relation",
            type: "text",
            rules: {
                required: "Relationship is required",
                pattern: {
                    value: /^[a-zA-Z\s]*$/,
                    message: "Numbers and special characters are not allowed",
                },
                minLength: { value: 2, message: "Name must be at least 2 characters" },
            },
        },
        {
            label: "Date of Birth",
            name: "dob",
            type: "date",
            rules: { required: "Date of Birth is required" },
        },
        {
            label: "Aadhaar No",
            name: "aadhaarNo",
            type: "text",
            rules: {
                required: "Aadhaar Number is required",
                pattern: { value: /^\d{12}$/, message: "Aadhaar must be 12 digits" },
            },
        },
        {
            label: "PAN No",
            name: "panNo",
            type: "text",
            rules: {
                required: "PAN Number is required",
                pattern: {
                    value: /[A-Z]{5}[0-9]{4}[A-Z]{1}/,
                    message: "Invalid PAN format",
                },
            },
        },
        {
            label: "Address",
            name: "address",
            type: "text",
            rules: { required: "Address is required" },
        },
    ];
    //     {
    //         label: "About",
    //         name: "about",
    //         type: "text",
    //         rules: {
    //             required: "About is required",
    //             minLength: { value: 2, message: "Min 2 characters required" }
    //         }
    //     }
    // ];

    const additionalFields = [
        {
            label: "Passport Number",
            name: "passportNumber",
            type: "text",
            placeholder: "Enter Passport Number",
            rules: { required: "Passport Number is required" }
        },
        {
            label: "Hobbies",
            name: "hobbiesDeclaration",
            type: "text",
            placeholder: "e.g. Cooking and exploring new recipes",
            rules: { required: "Hobbies are required" }
        },
        {
            label: "Illness",
            name: "illnessDeclaration",
            type: "text",
            placeholder: "Enter Illness Details",
            rules: { required: "Illness details are required" }
        },
    ];
    const professionalReferencesFields = [
        {
            label: "Reference Name",
            name: "refName",
            type: "text",
            placeholder: "Enter Name",
            rules: { required: "Reference Name is required" }
        },
        {
            label: "Email",
            name: "refEmail",
            type: "email",
            placeholder: "Enter Email",
            rules: {
                required: "Email is required",
                pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address"
                }
            }
        },
        {
            label: "Contact",
            name: "refContact",
            type: "text",
            placeholder: "Enter Contact Number",
            rules: { required: "Contact is required" }
        },
        {
            label: "Designation",
            name: "refDesignation",
            type: "text",
            placeholder: "Enter Designation",
            rules: { required: "Designation is required" }
        },
        {
            label: "Relationship",
            name: "refRelationship",
            type: "text",
            placeholder: "Enter Relationship",
            rules: { required: "Relationship is required" }
        },
        {
            label: "Employee ID",
            name: "refEmpId",
            type: "text",
            placeholder: "Enter Employee ID",
            rules: { required: "Employee ID is required" }
        },
        {
            label: "Department",
            name: "refDepartment",
            type: "text",
            placeholder: "Enter Department",
            rules: { required: "Department is required" }
        }
    ];

    const personalFields = [
        { label: "Father's Name", name: "fatherName", type: "text", rules: { required: "Required", pattern: { value: /^[a-zA-Z\s]*$/, message: "Only letters allowed" } } },
        { label: "Mother's Name", name: "motherName", type: "text", rules: { required: "Required", pattern: { value: /^[a-zA-Z\s]*$/, message: "Only letters allowed" } } },
        { label: "Aadhaar Number", name: "adhaarNo", type: "text", rules: { required: "Required", pattern: { value: /^\d{12}$/, message: "Must be exactly 12 digits" } } },
        { label: "PAN Number", name: "panNo", type: "text", rules: { required: "Required", pattern: { value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, message: "Invalid format (e.g., ABCDE1234F)" } } },
        { label: "UAN Number", name: "uanNo", type: "text", rules: { pattern: { value: /^\d{12}$/, message: "Must be exactly 12 digits" } } },
        { label: "Alt Mobile No.", name: "secondaryMobile", type: "text", rules: { pattern: { value: /^[6-9]\d{9}$/, message: "Invalid mobile number" } } },
        { label: "Religion", name: "religion", type: "text" },
        { label: "Gender", name: "gender", type: "select", options: ["Male", "Female", "Other"], rules: { required: "Required" } },
        { label: "Blood Group", name: "bloodGroup", type: "select", options: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"], rules: { required: "Required" } },
        { label: "Marital Status", name: "maritalStatus", type: "select", options: ["Single", "Married", "Divorced", "Widowed"], rules: { required: "Required" } },
        { label: "Employment of Spouse", name: "employmentOfSpouse", type: "text" },
        { label: "No. of Children", name: "noOfChildren", type: "number", rules: { max: { value: 15, message: "Invalid number" } } }
    ];
    const hasCategory = (fieldGroupName) => {
        if (!ticket?.subCategories || !ticket?.fieldApprovals) return true;
        const requestedGroups = ticket.subCategories.split(',');
        const approvedGroups = ticket.fieldApprovals.map(item => item?.fieldGroupName?.trim());
        if (isTicketCreated || isTicketInProgress) {
            return requestedGroups.includes(fieldGroupName);
        }
        else {
            return approvedGroups.includes(fieldGroupName);
        }
    };
    const toggleSection = (sectionName) => {
        setOpenSections((prev) => ({
            ...prev,
            [sectionName]: !prev[sectionName],
        }));
    };

    const handleEditClick = (e, type, data) => {
        e.stopPropagation();
        setModalType(type);
        const cleanNumbers = (val) => (val ? String(val).replace(/\D/g, "") : "");

        if (type === "family") {
            setModalTitle("Edit Family Information");
            setModalSize("lg");
            const activeFamilyData = data || displayData?.insuranceDependentDetails || [];
            reset({
                familyDetails: activeFamilyData.map((member) => ({
                    ...member,
                    dob: member.dob ? new Date(member.dob).toISOString().split("T")[0] : "",
                })),
            });
        }
        else if (type === "nominee") {
            setModalTitle("Edit Nominee Information");
            setCurrentFields(nomineeFields);
            setModalSize("md");
            const activeNominee = data || displayData?.nomineeDetails;
            reset({
                ...activeNominee,
                dob: activeNominee?.dob ? new Date(activeNominee.dob).toISOString().split("T")[0] : "",
                aadhaarNo: cleanNumbers(activeNominee?.aadhaarNo),
            });
        }
        else if (type === "bank") {
            setModalTitle("Edit Bank Information");
            setCurrentFields(bankFields);
            setModalSize("md");
            reset(data || displayData?.bankDetails);
        }
        else if (type === "address") {
            setModalTitle("Edit Current Address");
            setModalSize("md");
            setCurrentFields(addressFields);
            reset(data || currentAddressData);
        }
        else if (type === "experience") {
            setModalTitle("Add New Experience");
            setModalSize("lg");
            setCurrentFields(experienceFields);
            const activeHistory = data || displayData?.employmentHistory || [];
            reset({
                employmentHistory: activeHistory.length > 0
                    ? activeHistory.map((edu) => ({
                        ...edu,
                        // convert to ISO and split out the YYYY-MM-DD part
                        employmentPeriodFrom: edu.employmentPeriodFrom ? new Date(edu.employmentPeriodFrom).toISOString().split("T")[0] : "",
                        employmentPeriodTo: edu.employmentPeriodTo ? new Date(edu.employmentPeriodTo).toISOString().split("T")[0] : "",
                    }))
                    : [
                        {
                            previousEmployerName: "", department: "", designation: "", employeeCode: "",
                            employmentPeriodFrom: "", employmentPeriodTo: "", managerName: "",
                            mobileNumber: "", managerEmail: ""
                        }
                    ]
            });
        }
        else if (type === "education") {
            setModalTitle("Add Education Details");
            setModalSize("lg");
            setCurrentFields(educationFields);
            const activeEdu = data || displayData?.educationDetails || [];
            reset({
                educationDetails: activeEdu.length > 0
                    ? activeEdu.map((edu) => ({
                        ...edu,
                        // convert to ISO and split out the YYYY-MM-DD part
                        fromDate: edu.fromDate ? new Date(edu.fromDate).toISOString().split("T")[0] : "",
                        toDate: edu.toDate ? new Date(edu.toDate).toISOString().split("T")[0] : "",
                    }))
                    : [
                        { degreeName: "", fieldOfStudy: "", institutionName: "", gradeOrcgpa: "", fromDate: "", toDate: "", rollNumber: "", location: "" },
                    ]
            });
        }
        else if (type === "personal") {
            setModalTitle("Edit Personal Details");
            setModalSize("md");
            setCurrentFields(personalFields);
            const sourcePersonal = data || displayData?.personalDetail;
            reset({
                ...sourcePersonal,
                fatherName: sourcePersonal?.fatherName || "",
                motherName: sourcePersonal?.motherName || "",
                gender: sourcePersonal?.gender || "",
                personalEmail: sourcePersonal?.personalEmail || "",
                secondaryMobile: sourcePersonal?.secondaryMobile || "",
                adhaarNo: sourcePersonal?.adhaarNo || "",
                panNo: sourcePersonal?.panNo || "",
                uanNo: sourcePersonal?.uanNo || "",
                religion: sourcePersonal?.religion || "",
                bloodGroup: sourcePersonal?.bloodGroup || "",
                maritalStatus: sourcePersonal?.maritalStatus || "",
                employmentOfSpouse: sourcePersonal?.employmentOfSpouse || "",
                noOfChildren: sourcePersonal?.noOfChildren || "",
                emergencyContactNumber: sourcePersonal?.emergencyContactNumber || ""
            });
        }
        else if (type === "additional") {
            setModalTitle("Edit Additional Details");
            setModalSize("md");
            setCurrentFields(additionalFields);

            const sourceAdditional = data || displayData?.additionalDetails;          

            reset({
                // Map passport & fields directly from additionalDetails
                passportNumber: sourceAdditional?.passportNumber || "",
                hobbiesDeclaration: sourceAdditional?.hobbiesDeclaration || "",
                illnessDeclaration: sourceAdditional?.illnessDeclaration || "",
            });
        }
        else if (type === "professional") {
            setModalTitle("Edit Additional Details");
            setModalSize("md");
            setCurrentFields(professionalReferencesFields);

            const sourceReferences = displayData?.professionalReferences?.[0] || {};

            reset({
                // Safely extract and pull up reference details from the array into flat form fields
                refName: sourceReferences?.name || "",
                refEmail: sourceReferences?.email || "",
                refContact: sourceReferences?.contactNumber || "",
                refDesignation: sourceReferences?.designation || "",
                refRelationship: sourceReferences?.relationship || "",
                refEmpId: sourceReferences?.employeeId || "",
                refDepartment: sourceReferences?.department || ""
            });
        }

        setShowEditModal(true);
    };
    const onUpdate = (data) => {
        let sectionData = {};
        let currentSectionType = "";

        switch (modalType) {
            case "bank":
                currentSectionType = "BANK_DETAILS";
                sectionData = {
                    bankName: data.bankName,
                    accountNumber: data.accountNumber,
                    ifscCode: data.ifscCode,
                    accountHolderName: data.accountHolderName,
                    branchName: data.branchName
                };
                break;
            case "nominee":
                currentSectionType = "NOMINEE";
                sectionData = {
                    nomineeDetails: [
                        {
                            nomineeName: data.nomineeName,
                            relation: data.relation,
                            dob: data.dob,
                            aadhaarNo: data.aadhaarNo,
                            panNo: data.panNo,
                            address: data.address
                        }
                    ]
                };
                break;
            case "family":
                currentSectionType = "INSURANCE_DEPENDENT";
                // sectionData = {
                //     insuranceDependentDetails: (data.familyDetails || []).map((member) => ({
                //         name: member.name,
                //         relationship: member.relationship,
                //         dob: member.dob,
                //         phone: member.phone,
                //         gender: member.gender,
                //         age: member.age,
                //         amount: member.amount
                //     }))
                // };
                // Get the first item from your form array
                // Extract the first family member from React Hook Form's array field
                const familyMember = data.familyDetails?.[0] || {};

                sectionData = {
                    insuranceDependentDetails: [
                        {
                            // Include id if it exists so the backend can map to recordId if needed
                            id: familyMember.id || null,
                            name: familyMember.name || "",
                            relationship: familyMember.relationship || "",
                            dob: familyMember.dob || "",
                            phone: familyMember.phone || "",
                            gender: familyMember.gender || "",
                            age: familyMember.age || "",
                            amount: familyMember.amount || ""
                        }
                    ]
                };
                break;
           
            case "address":
                currentSectionType = "ADDRESS";               
                sectionData = {
                    address1: data.address1 || null,
                    address2: data.address2 || null,
                    address3: data.address3 || null,
                    city: data.city || null,
                    state: data.state || null,
                    zip: data.zip || null,
                    country: data.country || null,
                    addressType: "CURRENT"
                };
                break;
            case "experience":
                currentSectionType = "EXPERIENCE";
                sectionData = { employmentHistory: data.employmentHistory || [] };
                break;
            case "education":
                currentSectionType = "EDUCATION";
                sectionData = { educationDetails: data.educationDetails || [] };
                break;
            case "personal":
                currentSectionType = "PERSONAL_INFORMATION";
                sectionData = {
                    fatherName: data.fatherName,
                    motherName: data.motherName,
                    gender: data.gender,
                    personalEmail: data.personalEmail,
                    secondaryMobile: data.secondaryMobile,
                    adhaarNo: data.adhaarNo,
                    panNo: data.panNo,
                    uanNo: data.uanNo,
                    religion: data.religion,
                    bloodGroup: data.bloodGroup,
                    maritalStatus: data.maritalStatus,
                    employmentOfSpouse: data.employmentOfSpouse,
                    noOfChildren: data.noOfChildren,
                    emergencyContactNumber: data.emergencyContactNumber
                };
                // sectionData = {
                //     personalDetail: {
                //         ...displayData?.personalDetail,
                //         ...data
                //     }
                // };
                break;
            case "additional":
                currentSectionType = "ADDITIONAL_DETAILS";
                sectionData = {
                    passportNumber: data.passportNumber,
                    hobbiesDeclaration: data.hobbiesDeclaration,
                    illnessDeclaration: data.illnessDeclaration,
                };
                break;
            case "professional":
                currentSectionType = "PROFESSIONAL_REFERENCE";
                sectionData = {
                    // Nest the reference flat form fields back into the array format the backend uses
                    professionalReferences: [
                        {
                            name: data.refName,
                            email: data.refEmail,
                            contactNumber: data.refContact,
                            designation: data.refDesignation,
                            relationship: data.refRelationship,
                            employeeId: data.refEmpId,
                            department: data.refDepartment,
                            isRelative: false
                        }
                    ]
                };
                break;
            default:
                currentSectionType = modalType.toUpperCase();
                sectionData = data;
                break;
        }

        // Overwrite or append the section updates to prevent duplicate entries of the same section
        setAccumulatedSections((prev) => {
            const filtered = prev.filter(item => item.sectionType !== currentSectionType);
            return [
                ...filtered,
                {
                    sectionType: currentSectionType,
                    // recordId: ticket.createdByUserId || null,
                    data: sectionData
                }
            ];
        });
        toast.success("Use the main submit button to save updates and see the results!");
        setShowEditModal(false);
    };

    const handleFinalSubmit = async () => {
        if (accumulatedSections.length === 0) return;

        const remarks = `Profile structural update request for: ${accumulatedSections.map(s => s.sectionType).join(', ')}`;
        const finalPayload = { sections: accumulatedSections, remarks };

        try {
            const response = await dispatch(createProfileData({ id: ticketId, payload: finalPayload })).unwrap();

            if (response?.success === true) {
                toast.success(response?.reason || "Profile Data updated successfully!", { className: 'toast-success-custom' });
                setAccumulatedSections([]);

                if (ticketId) {
                    setTimeout(() => {
                        dispatch(fetchTicketById(ticketId));
                        dispatch(fetchProfileData(ticketId));
                    }, 1000);
                }
            } else {
                const cleanReason = response?.reason ? response.reason.replace(/_/g, " ") : "Validation failed!";
                toast.error(cleanReason, { className: "toast-error-custom" });
            }
        } catch (error) {
            const fallbackReason = error?.data?.reason || "profile update failed!";
            const cleanErrorReason = fallbackReason.replace(/_/g, " ");
            toast.error(cleanErrorReason, { className: "toast-error-custom" });
        }
    };

    // Condition : all section updates are completed 
    const hasPendingUpdates = accumulatedSections.length === ticket.fieldApprovals.length;

    // Combined Validation: Enable final submit button only when status matches and changes exist
    const isSubmitEnabled = (isTicketValid || isTicketRework) && hasPendingUpdates;

    return (
        <div className="timeline-content p-3">
            {showEdit && <h5 className="text-center p-2 text-danger">All approved fields must be updated to submit it further!</h5>}
            {hitMsg ? <p className="text-center text-danger py-2">You can see the updates in your profile page!</p> : (
                <>
                    {hasCategory("PERSONAL_INFORMATION") &&
                        <Card className="shadow-sm border-1 rounded-3 mb-3">
                            <Card.Body className="p-0">
                                <Row
                                    className="align-items-center m-0 cursor-pointer"
                                    onClick={() => toggleSection("personal")}
                                >
                                    <Col className="col-heading p-3">
                                        <h5 className="mb-0">Personal Information</h5>
                                    </Col>
                                    <Col xs="auto" className="d-flex align-items-center gap-3">
                                        {showEdit && (<i
                                            className="bi bi-pencil fs-6 mr-4"
                                            onClick={(e) =>
                                                handleEditClick(e, "personal", displayData?.personalDetail)
                                            }
                                        ></i>)}
                                        {openSections.personal ? (
                                            <BsChevronUp size={16} />
                                        ) : (
                                            <BsChevronDown size={16} />
                                        )}
                                    </Col>
                                </Row>
                                <Collapse in={openSections.personal}>
                                    <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-4">
                                        <div className="row">
                                            <InfoRowPersonal
                                                label="Father"
                                                value={displayData?.personalDetail?.fatherName}
                                            />
                                            <InfoRowPersonal
                                                label="Mother"
                                                value={displayData?.personalDetail?.motherName}
                                            />
                                            <InfoRowPersonal
                                                label="Aadhaar"
                                                value={displayData?.personalDetail?.adhaarNo}
                                            />
                                            <InfoRowPersonal
                                                label="PAN"
                                                value={displayData?.personalDetail?.panNo}
                                            />
                                            <InfoRowPersonal
                                                label="UAN"
                                                value={displayData?.personalDetail?.uanNo}
                                            />
                                            <InfoRowPersonal
                                                label="Religion"
                                                value={displayData?.personalDetail?.religion}
                                            />
                                            <InfoRowPersonal
                                                label="Blood Group"
                                                value={displayData?.personalDetail?.bloodGroup}
                                            />
                                            <InfoRowPersonal
                                                label="Gender"
                                                value={displayData?.personalDetail?.gender}
                                            />
                                            <InfoRowPersonal
                                                label="Marital Status"
                                                value={displayData?.personalDetail?.maritalStatus}
                                            />
                                            <InfoRowPersonal
                                                label="Employment of Spouse"
                                                value={displayData?.personalDetail?.employmentOfSpouse}
                                            />
                                            <InfoRowPersonal
                                                label="No. of Children"
                                                value={displayData?.personalDetail?.noOfChildren}
                                            />
                                            <InfoRowPersonal
                                                label="Nationality"
                                                value={displayData?.addresses?.[0]?.nationality}
                                            />
                                            <InfoRowPersonal
                                                label="Alternative Mob. No."
                                                value={displayData?.personalDetail?.secondaryMobile}
                                            />
                                        </div>
                                    </div>
                                </Collapse>
                            </Card.Body>
                        </Card>}

                    {hasCategory("BANK_DETAILS") &&
                        <Card className="shadow-sm border-1 rounded-3 mb-3">
                            <Card.Body className="p-0">
                                <Row
                                    className="align-items-center m-0 cursor-pointer"
                                    onClick={() => toggleSection("bank")} >
                                    <Col className="col-heading p-3">
                                        <h5 className="mb-0">Bank Information</h5>
                                    </Col>
                                    <Col xs="auto" className="d-flex align-items-center gap-3">
                                        {showEdit && (<i
                                            className="bi bi-pencil fs-6 mr-4"
                                            onClick={(e) =>
                                                handleEditClick(e, "bank", displayData?.bankDetails)
                                            }
                                        ></i>)}
                                        {openSections.bank ? (
                                            <BsChevronUp size={16} />
                                        ) : (
                                            <BsChevronDown size={16} />
                                        )}
                                    </Col>
                                </Row>

                                <Collapse in={openSections.bank}>
                                    <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-4">
                                        <InfoRow
                                            label="Account Holder Name"
                                            value={displayData?.bankDetails?.accountHolderName}
                                        />
                                        <InfoRow
                                            label="Account Number"
                                            value={displayData?.bankDetails?.accountNumber}
                                        />
                                        <InfoRow
                                            label="Bank Name"
                                            value={displayData?.bankDetails?.bankName}
                                        />
                                        <InfoRow
                                            label="IFSC Code"
                                            value={displayData?.bankDetails?.ifscCode}
                                        />
                                        <InfoRow
                                            label="Branch Name"
                                            value={displayData?.bankDetails?.branchName}
                                        />
                                    </div>
                                </Collapse>
                            </Card.Body>
                        </Card>
                    }

                    {hasCategory("INSURANCE_DEPENDENT") &&
                        <Card className="shadow-sm border-1 rounded-3 mb-3">
                            <Card.Body className="p-0">
                                <Row
                                    className="align-items-center m-0 cursor-pointer"
                                    onClick={() => toggleSection("family")}
                                >
                                    <Col className="col-heading p-3">
                                        <h5 className="mb-0">
                                            Insurance Dependent Details
                                        </h5>
                                    </Col>
                                    <Col xs="auto" className="d-flex align-items-center gap-3">
                                        {showEdit && (<i
                                            className="bi bi-pencil fs-6 mr-4"
                                            onClick={(e) =>
                                                handleEditClick(e, "family", displayData?.insuranceDependentDetails)
                                            }
                                        ></i>)}
                                        {openSections.family ? (
                                            <BsChevronUp size={16} />
                                        ) : (
                                            <BsChevronDown size={16} />
                                        )}
                                    </Col>
                                </Row>
                                <Collapse in={openSections.family}>
                                    <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-4">
                                        <hr className="w-100" />
                                        {Array.isArray(displayData?.insuranceDependentDetails) &&
                                            displayData.insuranceDependentDetails.length > 0 ? (
                                            displayData.insuranceDependentDetails.map((member, index) => (
                                                <div key={member?.id ?? index}>
                                                    <div className="row">
                                                        <div className="col-md-3 col-6 text-muted profile-label">Name:</div>
                                                        <div className="col-md-3 col-6 text-theme">
                                                            {getDisplayValue(member?.name)}
                                                        </div>
                                                        <div className="col-md-3 col-6 text-muted profile-label">Relationship:</div>
                                                        <div className="col-md-3 col-6 text-theme">
                                                            {getDisplayValue(member?.relationship)}
                                                        </div>

                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-3 col-6 text-muted profile-label">Date of Birth:</div>
                                                        <div className="col-md-3 col-6 text-theme">
                                                            {formatDate(member?.dob)}
                                                        </div>
                                                        <div className="col-md-3 col-6 text-muted profile-label">Gender:</div>
                                                        <div className="col-md-3 col-6 text-theme">
                                                            {getDisplayValue(member?.gender)}
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-3 col-6 text-muted profile-label">Age:</div>
                                                        <div className="col-md-3 col-6 text-theme">
                                                            {getDisplayValue(member?.age)}
                                                        </div>
                                                        <div className="col-md-3 col-6 text-muted profile-label">Phone:</div>
                                                        <div className="col-md-3 col-6 text-theme">
                                                            {getDisplayValue(member?.phone)}
                                                        </div>

                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-3 col-3 text-muted profile-label">SI Amount:</div>
                                                        <div className="col-md-3 col-3 text-theme">
                                                            {getDisplayValue(member?.amount)}
                                                        </div>
                                                    </div>
                                                    {index !== displayData.insuranceDependentDetails.length - 1 && (
                                                        <hr className="w-100" />
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-muted text-center py-2">
                                                No insurance dependent details available
                                            </div>
                                        )}
                                    </div>
                                </Collapse>
                            </Card.Body>
                        </Card>
                    }

                    {hasCategory("NOMINEE") &&
                        <Card className="shadow-sm border-1 rounded-3 mb-3">
                            <Card.Body className="p-0">
                                <Row
                                    className="align-items-center m-0 cursor-pointer"
                                    onClick={() => toggleSection("nominee")}
                                >
                                    <Col className="col-heading p-3">
                                        <h5 className="mb-0">Nominee Information</h5>
                                    </Col>
                                    <Col xs="auto" className="d-flex align-items-center gap-3">
                                        {showEdit && (<i
                                            className="bi bi-pencil fs-3 mr-4 cursor-pointer"
                                            onClick={(e) =>
                                                handleEditClick(
                                                    e,
                                                    "nominee",
                                                    displayData?.nomineeDetails?.[0],
                                                )
                                            }
                                        ></i>)}
                                        {openSections.nominee ? (
                                            <BsChevronUp size={16} />
                                        ) : (
                                            <BsChevronDown size={16} />
                                        )}
                                    </Col>
                                </Row>
                                <Collapse in={openSections.nominee}>
                                    <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-2">
                                        <hr className="w-100" />
                                        {Array.isArray(displayData?.nomineeDetails) &&
                                            displayData.nomineeDetails.length > 0 ? (
                                            displayData.nomineeDetails.map((member, index) => (
                                                <div key={member?.id ?? index}>
                                                    <div className="row">
                                                        <div className="col-md-3 col-6 text-muted profile-label">
                                                            Name:
                                                        </div>

                                                        <div className="col-md-3 col-6 text-theme">
                                                            {getDisplayValue(member?.nomineeName)}
                                                        </div>

                                                        <div className="col-md-3 col-6 text-muted profile-label">
                                                            Relationship:
                                                        </div>

                                                        <div className="col-md-3 col-6 text-theme">
                                                            {getDisplayValue(member?.relation)}
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-3 col-6 text-muted profile-label">
                                                            Date of Birth:
                                                        </div>

                                                        <div className="col-md-3 col-6 text-theme">
                                                            {formatDate(member?.dob)}
                                                        </div>

                                                        <div className="col-md-3 col-6 text-muted profile-label">
                                                            Aadhaar No:
                                                        </div>

                                                        <div className="col-md-3 col-6 text-theme">
                                                            {getDisplayValue(member?.aadhaarNo)}
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-3 col-6 text-muted profile-label">
                                                            PAN No:
                                                        </div>
                                                        <div className="col-md-3 col-6 text-theme">
                                                            {getDisplayValue(member?.panNo)}
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-md-3 col-4 text-muted profile-label">
                                                            Address:
                                                        </div>
                                                        <div className="col-md-9 col-12 text-theme">
                                                            {getDisplayValue(member?.address)}
                                                        </div>
                                                    </div>
                                                    {index !== displayData.nomineeDetails.length - 1 && (
                                                        <hr className="w-100" />
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-muted text-center py-2">
                                                No nominee details available
                                            </div>
                                        )}
                                    </div>
                                </Collapse>
                            </Card.Body>
                        </Card>
                    }

                    {hasCategory("EDUCATION") &&
                        <Card className="shadow-sm border-1 rounded-3 mb-3">
                            <Card.Body className="p-0">
                                <Row
                                    className="align-items-center m-0 cursor-pointer"
                                    onClick={() => toggleSection("education")}
                                >
                                    <Col className="col-heading p-3">
                                        <h5 className="mb-0">Education Details</h5>
                                    </Col>
                                    <Col xs="auto" className="d-flex align-items-center gap-3">
                                        {showEdit && (<i
                                            className="bi bi-pencil fs-3 mr-4 cursor-pointer"
                                            onClick={(e) => handleEditClick(e, "education", null)}
                                        ></i>)}
                                        {openSections.education ? (
                                            <BsChevronUp size={16} />
                                        ) : (
                                            <BsChevronDown size={16} />
                                        )}
                                    </Col>
                                </Row>
                                <Collapse in={openSections.education}>
                                    <div className="mt-3 my-2 px-3 text-secondary ps-4 pe-3 pb-4">
                                        {(displayData?.educationDetails ?? []).length > 0 ? (
                                            displayData.educationDetails.map((education) => {
                                                const startYear = extractYear(education?.fromDate);
                                                const endYear = extractYear(education?.toDate);
                                                const displayDuration = (startYear && endYear && startYear !== "null" && endYear !== "null")
                                                    ? `${startYear} - ${endYear}`
                                                    : "N/A";
                                                // Safe Qualification element fallback
                                                const qualificationValue = education?.degreeName?.trim() ? (
                                                    <>
                                                        {education.degreeName}
                                                        {education?.gradeOrcgpa && (
                                                            <span className="badge text-dark ms-2">
                                                                Grade/Cgpa: {education.gradeOrcgpa}
                                                            </span>
                                                        )}
                                                    </>
                                                ) : "N/A";

                                                // safe institution fallback
                                                const instituteValue = education?.institutionName?.trim() ? (
                                                    <>
                                                        {education.institutionName}
                                                        {education?.location && (
                                                            <span className="badge text-dark ms-2">
                                                                {education.location}
                                                            </span>
                                                        )}
                                                    </>
                                                ) : "N/A";
                                                return (
                                                    <div
                                                        className="border-bottom pb-3 mb-3"
                                                        key={education.id}
                                                    >
                                                        <div className="row">
                                                            <InfoRowPersonal
                                                                label="Qualification"
                                                                value={qualificationValue}
                                                            />
                                                            <InfoRowPersonal
                                                                label="Institute"
                                                                value={instituteValue}
                                                            />
                                                            <InfoRowPersonal
                                                                label="Field Of Study"
                                                                value={education?.fieldOfStudy}
                                                            />
                                                            <InfoRowPersonal
                                                                label="Duration"
                                                                value={displayDuration}
                                                            />
                                                            <InfoRowPersonal
                                                                label="Roll Number"
                                                                value={education?.rollNumber}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="text-muted text-center py-2">
                                                No educational details available
                                            </div>
                                        )}
                                    </div>
                                </Collapse>
                            </Card.Body>
                        </Card>
                    }

                    {hasCategory("EXPERIENCE") &&
                        <Card className="shadow-sm border-1 rounded-3 mb-3">
                            <Card.Body className="p-0">
                                <Row
                                    className="align-items-center m-0 cursor-pointer"
                                    onClick={() => toggleSection("experience")}
                                >
                                    <Col className="col-heading p-3">
                                        <h5 className="mb-0">Experience</h5>
                                    </Col>
                                    <Col xs="auto" className="d-flex align-items-center gap-3">
                                        {showEdit && (<i
                                            className="bi bi-pencil fs-3 mr-4 cursor-pointer"
                                            onClick={(e) => handleEditClick(e, "experience", null)}
                                        ></i>)}
                                        {openSections.experience ? (
                                            <BsChevronUp size={16} />
                                        ) : (
                                            <BsChevronDown size={16} />
                                        )}
                                    </Col>
                                </Row>
                                <Collapse in={openSections.experience}>
                                    <div className="mt-3 my-2 px-3 text-secondary ps-4 pe-3 pb-4">
                                        {(displayData?.employmentHistory ?? []).length > 0 ? (
                                            displayData.employmentHistory.map((exp) => {
                                                const startYear = extractYear(exp?.employmentPeriodFrom);
                                                const endYear = extractYear(exp?.employmentPeriodTo);
                                                return (
                                                    <div className="border-bottom pb-3 mb-3" key={exp.id}>
                                                        <div className="row">
                                                            <InfoRowPersonal
                                                                label="Employer"
                                                                value={exp?.previousEmployerName}
                                                                className="exp-employer"
                                                            />
                                                            <InfoRowPersonal
                                                                label="Designation"
                                                                value={exp?.designation}
                                                                className="exp-designation"
                                                            />
                                                            <InfoRowPersonal
                                                                label="Emp Code"
                                                                value={exp?.employeeCode}
                                                                className="exp-empcode"
                                                            />
                                                            <InfoRowPersonal
                                                                label="Department"
                                                                value={exp?.department}
                                                                className="exp-department"
                                                            />
                                                            <InfoRowPersonal
                                                                label="Manager Name"
                                                                value={exp?.managerName}
                                                                className="exp-manager"
                                                            />
                                                            <InfoRowPersonal
                                                                label="Manager Contact"
                                                                value={
                                                                    // If BOTH mobile number and email are missing, pass "" to trigger your N/A logic
                                                                    !(exp?.mobileNumber || exp?.managerEmail) ? "" : (
                                                                        <span className="badge text-dark exp-manager-contact bg-light border">
                                                                            {[exp?.mobileNumber, exp?.managerEmail].filter(Boolean).join(" | ")}
                                                                        </span>
                                                                    )
                                                                }
                                                                className="exp-manager"
                                                            />
                                                            <InfoRowPersonal
                                                                label="Duration"
                                                                value={getFormattedDuration(
                                                                    exp?.employmentPeriodFrom,
                                                                    exp?.employmentPeriodTo,
                                                                )}
                                                                className="exp-duration"
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="text-muted text-center">
                                                No experience records found
                                            </div>
                                        )}
                                    </div>
                                </Collapse>
                            </Card.Body>
                        </Card>
                    }

                    {hasCategory("ADDRESS") && (
                        <Card className="shadow-sm border-1 rounded-3 mb-3">
                            <Card.Body className="p-0">
                                <Row
                                    className="align-items-center m-0 cursor-pointer"
                                    onClick={() => toggleSection("address")}
                                >
                                    <Col className="col-heading p-3">
                                        <h5 className="mb-0">Address</h5>
                                    </Col>
                                    <Col xs="auto" className="d-flex align-items-center gap-3">
                                        {showEdit && (
                                            <i
                                                className="bi bi-pencil fs-3 mr-4 cursor-pointer"
                                                onClick={(e) =>
                                                    handleEditClick(
                                                        e,
                                                        "address",
                                                        currentAddressData || displayData?.addresses?.find(addr => addr?.addressType !== "PERMANENT")
                                                    )
                                                }
                                            ></i>
                                        )}
                                        {openSections.address ? (
                                            <BsChevronUp size={16} />
                                        ) : (
                                            <BsChevronDown size={16} />
                                        )}
                                    </Col>
                                </Row>
                                <Collapse in={openSections.address}>
                                    <div className="p-3">
                                        <strong>
                                            <i className="bi bi-geo-alt me-1" /> Current Address:
                                        </strong>
                                        <p>{joinAddress(currentAddressData) || "No current address provided"}</p>
                                    </div>
                                </Collapse>
                            </Card.Body>
                        </Card>
                    )}

                    {hasCategory("ADDITIONAL_DETAILS") &&
                        <Card className="shadow-sm border-1 rounded-3 mb-3">
                            <Card.Body className="p-0">
                                <Row
                                    className="align-items-center m-0 cursor-pointer"
                                    onClick={() => toggleSection("additional")}
                                >
                                    <Col className="col-heading p-3">
                                        <h5 className="mb-0">Additional Information</h5>
                                    </Col>
                                    <Col xs="auto" className="d-flex align-items-center gap-3">
                                        {showEdit && (<i
                                            className="bi bi-pencil fs-6 mr-4"
                                            onClick={(e) =>
                                                handleEditClick(e, "additional", displayData?.additionalDetails)
                                            }
                                        ></i>)}
                                        {openSections.additional ? (
                                            <BsChevronUp size={16} />
                                        ) : (
                                            <BsChevronDown size={16} />
                                        )}
                                    </Col>
                                </Row>

                                <Collapse in={openSections.additional}>
                                    <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-4">
                                        <div className="row">
                                            <div className="col-md-12 d-flex align-items-center justify-content-between mb-2">
                                                <span className="profile-label d-inline-flex align-items-center">
                                                    <i className="bi bi-passport me-1" /> Passport Number:
                                                </span>
                                                <p className="profile-label-value text-dark m-0 text-end">
                                                    {getDisplayValue(
                                                        displayData?.additionalDetails?.passportNumber,
                                                    )}
                                                </p>
                                            </div>

                                            <div className="col-md-12 d-flex align-items-center justify-content-between mb-2">
                                                <span className="profile-label d-inline-flex align-items-center">
                                                    <i className="bi bi-passport me-1" /> Hobbies:
                                                </span>
                                                <p className="profile-label-value text-dark m-0">
                                                    {getDisplayValue(
                                                        displayData?.additionalDetails?.hobbiesDeclaration,
                                                    )}
                                                </p>
                                            </div>

                                            <div className="col-md-12 d-flex align-items-center justify-content-between mb-2">
                                                <span className="profile-label d-inline-flex align-items-center">
                                                    <i className="bi bi-passport me-1" /> Illness:
                                                </span>
                                                <p className="profile-label-value text-dark m-0">
                                                    {getDisplayValue(
                                                        displayData?.additionalDetails?.illnessDeclaration,
                                                    )}
                                                </p>
                                            </div>                                           
                                        </div>
                                    </div>
                                </Collapse>
                            </Card.Body>
                        </Card>
                    }

                    {hasCategory("PROFESSIONAL_REFERENCE") &&
                        <Card className="shadow-sm border-1 rounded-3 mb-3">
                            <Card.Body className="p-0">
                                <Row
                                    className="align-items-center m-0 cursor-pointer"
                                    onClick={() => toggleSection("professional")}
                                >
                                    <Col className="col-heading p-3">
                                        <h5 className="mb-0">Professional Reference</h5>
                                    </Col>
                                    <Col xs="auto" className="d-flex align-items-center gap-3">
                                        {showEdit && (<i
                                            className="bi bi-pencil fs-6 mr-4"
                                            onClick={(e) =>
                                                handleEditClick(e, "professional", displayData?.professionalReferences)
                                            }
                                        ></i>)}
                                        {openSections.professional ? (
                                            <BsChevronUp size={16} />
                                        ) : (
                                            <BsChevronDown size={16} />
                                        )}
                                    </Col>
                                </Row>

                                <Collapse in={openSections.professional}>
                                    <div className="mt-3 my-2 px-3 text-secondary ps-3 pe-3 pb-4">
                                        <div className="row">
                                                                                      
                                            <div className="col-md-12 mt-3">
                                                {(displayData?.professionalReferences ?? []).length >
                                                    0 ? (
                                                    <div className="row">
                                                        <InfoRowPersonal
                                                            label="Name"
                                                            value={
                                                                displayData?.professionalReferences?.[0]?.name
                                                            }
                                                        />
                                                        <InfoRowPersonal
                                                            label="Email"
                                                            value={
                                                                displayData?.professionalReferences?.[0]?.email
                                                            }
                                                        />
                                                        <InfoRowPersonal
                                                            label="Contact"
                                                            value={
                                                                displayData?.professionalReferences?.[0]
                                                                    ?.contactNumber
                                                            }
                                                        />
                                                        <InfoRowPersonal
                                                            label="Designation"
                                                            value={
                                                                displayData?.professionalReferences?.[0]
                                                                    ?.designation
                                                            }
                                                        />
                                                        <InfoRowPersonal
                                                            label="Relationship"
                                                            value={
                                                                displayData?.professionalReferences?.[0]
                                                                    ?.relationship
                                                            }
                                                        />
                                                        <InfoRowPersonal
                                                            label="Employee ID"
                                                            value={
                                                                displayData?.professionalReferences?.[0]
                                                                    ?.employeeId
                                                            }
                                                        />
                                                        <InfoRowPersonal
                                                            label="Department"
                                                            value={
                                                                displayData?.professionalReferences?.[0]
                                                                    ?.department
                                                            }
                                                        />
                                                    </div>
                                                ) : (
                                                    <p className="text-muted text-center">
                                                        No professional references found.
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </Collapse>
                            </Card.Body>
                        </Card>
                    }

                    <div className="mt-2" style={{ textAlign: "center" }}>
                        <button
                            type="submit"
                            className="primary-btn"
                            disabled={!isSubmitEnabled}
                            onClick={handleFinalSubmit}
                            style={{ opacity: isSubmitEnabled ? 1 : 0.6, cursor: isSubmitEnabled ? 'pointer' : 'not-allowed' }}
                        >
                            Submit Updates {hasPendingUpdates && `(${accumulatedSections.length})`}
                        </button>
                    </div>
                    <EmployeeEditModal
                        show={showEditModal}
                        onHide={() => setShowEditModal(false)}
                        type={modalType}
                        title={modalTitle}
                        size={modalSize}
                        control={control}
                        handleSubmit={handleSubmit}
                        onUpdate={onUpdate}
                        register={register}
                        errors={errors}
                        editFields={currentFields}
                        canEditCandidate={true}
                    />
                    <ToastContainer />
                </>)}
        </div>

    )
}
